import webBackApi from "../../api/webBackApi";

// Va al webback (JWT Bearer vía interceptor). Proxy hacia el dispositivo ROP
// identificado por su `uid` (Camara.camaraUID), asignado durante el escaneo QR
// del wizard de alta de cámara.

export const moveZoom = async (uid, action) => {
  try {
    const response = await webBackApi.patch(`/api/rop/devices/${uid}/move/zoom`, { action });
    return response.data;
  } catch (error) {
    console.error("Error moving zoom:", error);
    throw error;
  }
};

export const moveFocus = async (uid, action) => {
  try {
    const response = await webBackApi.patch(`/api/rop/devices/${uid}/move/focus`, { action });
    return response.data;
  } catch (error) {
    console.error("Error moving focus:", error);
    throw error;
  }
};

export const moveRotate = async (uid, { direction, speed, channel = 0 }) => {
  try {
    const response = await webBackApi.post(`/api/rop/devices/${uid}/move/rotate`, {
      channel,
      direction,
      speed,
    });
    return response.data;
  } catch (error) {
    console.error("Error rotating camera:", error);
    throw error;
  }
};

export const getRecordingsCalendar = async (uid, { channel = 0, year, month }) => {
  try {
    const response = await webBackApi.get(`/api/rop/devices/${uid}/media/records/calendar`, {
      params: { channel, year, month },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching recordings calendar:", error);
    throw error;
  }
};

export const getRecordingsList = async (
  uid,
  { channel = 0, streamType = "mainStream", recordDate, startAt, endAt, nextToken }
) => {
  try {
    const params = { channel, streamType, recordDate, startAt, endAt };
    if (nextToken) {
      params.nextToken = nextToken;
    }
    const response = await webBackApi.get(`/api/rop/devices/${uid}/media/records`, { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching recordings list:", error);
    throw error;
  }
};

export const getSnapshot = async (uid, { channel = 0, streamType = "mainStream" } = {}) => {
  try {
    const response = await webBackApi.get(`/api/rop/devices/${uid}/media/snapshot`, {
      params: { channel, streamType },
      responseType: "blob",
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching snapshot:", error);
    throw error;
  }
};

// ==== Fase 4: Ver en vivo (HLS) ====

// Starts a brand-new MediaMTX HLS session for the device (not idempotent: every
// call mints a new opaque token/path). The response's `expiresInSeconds` is
// always 60 — the caller is responsible for refreshing before then. Not queued
// via enqueueDeviceRequest: this is a one-shot session start, not a burst of
// settings calls.
export const startLiveHlsSession = async (uid, { channel = 0, streamType = "mainStream" } = {}) => {
  try {
    const response = await webBackApi.post(`/api/rop/devices/${uid}/live/hls-session`, {
      channel,
      streamType,
    });
    return response.data;
  } catch (error) {
    console.error("Error starting live HLS session:", error);
    throw error;
  }
};

// ==== Fase 3: Configuración (FTP, horarios, OSD, zona horaria) ====

// ROP devices (4G ones especially, which can go briefly unresponsive between
// keep-alives) reply unreliably when hit with a burst of simultaneous
// requests — the config tab mounts ~11 cards at once, each firing its own
// settings GET independently. This queues every settings GET/PUT/test/format
// below behind the previous one for the same device, so the camera only ever
// sees one in-flight config request at a time. Different devices still run
// fully in parallel: the queue is keyed by uid, not global.
const deviceQueues = new Map();

function enqueueDeviceRequest(uid, task) {
  const previous = deviceQueues.get(uid) || Promise.resolve();
  const run = previous.then(task, task);
  // Never let one failed request poison the queue for the ones behind it.
  deviceQueues.set(uid, run.catch(() => undefined));
  return run;
}

export const getFtpCaptureConfig = async (uid) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.get(`/api/rop/devices/${uid}/settings/ftp-capture`);
      return response.data;
    } catch (error) {
      console.error("Error fetching FTP capture config:", error);
      throw error;
    }
  });

export const updateFtpCaptureConfig = async (uid, body) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.put(`/api/rop/devices/${uid}/settings/ftp-capture`, body);
      return response.data;
    } catch (error) {
      console.error("Error updating FTP capture config:", error);
      throw error;
    }
  });

export const testFtpConnectivity = async (uid, body) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.post(`/api/rop/devices/${uid}/settings/ftp-capture/test`, body);
      return response.data;
    } catch (error) {
      console.error("Error testing FTP connectivity:", error);
      throw error;
    }
  });

export const getFtpSchedule = async (uid, channelId) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.get(`/api/rop/devices/${uid}/settings/ftp-schedule`, {
        params: { channelId },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching FTP schedule:", error);
      throw error;
    }
  });

export const updateFtpSchedule = async (uid, body) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.put(`/api/rop/devices/${uid}/settings/ftp-schedule`, body);
      return response.data;
    } catch (error) {
      console.error("Error updating FTP schedule:", error);
      throw error;
    }
  });

export const getOsdConfig = async (uid, channelId) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.get(`/api/rop/devices/${uid}/settings/osd`, {
        params: { channelId },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching OSD config:", error);
      throw error;
    }
  });

export const updateOsdConfig = async (uid, body) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.put(`/api/rop/devices/${uid}/settings/osd`, body);
      return response.data;
    } catch (error) {
      console.error("Error updating OSD config:", error);
      throw error;
    }
  });

export const getRecordingSchedule = async (uid, channelId) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.get(`/api/rop/devices/${uid}/settings/recording-schedule`, {
        params: { channelId },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching recording schedule:", error);
      throw error;
    }
  });

export const updateRecordingSchedule = async (uid, body) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.put(`/api/rop/devices/${uid}/settings/recording-schedule`, body);
      return response.data;
    } catch (error) {
      console.error("Error updating recording schedule:", error);
      throw error;
    }
  });

export const getTimezone = async (uid) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.get(`/api/rop/devices/${uid}/settings/timezone`);
      return response.data;
    } catch (error) {
      console.error("Error fetching timezone:", error);
      throw error;
    }
  });

export const updateTimezone = async (uid, body) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.put(`/api/rop/devices/${uid}/settings/timezone`, body);
      return response.data;
    } catch (error) {
      console.error("Error updating timezone:", error);
      throw error;
    }
  });

export const getPirConfig = async (uid) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.get(`/api/rop/devices/${uid}/settings/pir-config`);
      return response.data;
    } catch (error) {
      console.error("Error fetching PIR config:", error);
      throw error;
    }
  });

export const updatePirConfig = async (uid, body) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.put(`/api/rop/devices/${uid}/settings/pir-config`, body);
      return response.data;
    } catch (error) {
      console.error("Error updating PIR config:", error);
      throw error;
    }
  });

export const getLedState = async (uid) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.get(`/api/rop/devices/${uid}/settings/led-state`);
      return response.data;
    } catch (error) {
      console.error("Error fetching LED state:", error);
      throw error;
    }
  });

export const updateLedState = async (uid, body) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.put(`/api/rop/devices/${uid}/settings/led-state`, body);
      return response.data;
    } catch (error) {
      console.error("Error updating LED state:", error);
      throw error;
    }
  });

export const getGeneralConfig = async (uid) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.get(`/api/rop/devices/${uid}/settings/general-config`);
      return response.data;
    } catch (error) {
      console.error("Error fetching general config:", error);
      throw error;
    }
  });

export const updateGeneralConfig = async (uid, body) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.put(`/api/rop/devices/${uid}/settings/general-config`, body);
      return response.data;
    } catch (error) {
      console.error("Error updating general config:", error);
      throw error;
    }
  });

export const getAlarmConfig = async (uid) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.get(`/api/rop/devices/${uid}/settings/alarm`);
      return response.data;
    } catch (error) {
      console.error("Error fetching alarm config:", error);
      throw error;
    }
  });

export const updateAlarmConfig = async (uid, body) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.put(`/api/rop/devices/${uid}/settings/alarm`, body);
      return response.data;
    } catch (error) {
      console.error("Error updating alarm config:", error);
      throw error;
    }
  });

export const getBatteryStatus = async (uid) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.get(`/api/rop/devices/${uid}/settings/battery`);
      return response.data;
    } catch (error) {
      console.error("Error fetching battery status:", error);
      throw error;
    }
  });

export const getMobileNetwork = async (uid) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.get(`/api/rop/devices/${uid}/settings/mobile-network`);
      return response.data;
    } catch (error) {
      console.error("Error fetching mobile network info:", error);
      throw error;
    }
  });

export const getSdCard = async (uid) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.get(`/api/rop/devices/${uid}/settings/sd-card`);
      return response.data;
    } catch (error) {
      console.error("Error fetching SD card info:", error);
      throw error;
    }
  });

export const formatSdCard = async (uid, body) =>
  enqueueDeviceRequest(uid, async () => {
    try {
      const response = await webBackApi.post(`/api/rop/devices/${uid}/settings/sd-card/format`, body);
      return response.data;
    } catch (error) {
      console.error("Error formatting SD card:", error);
      throw error;
    }
  });

// ==== Device initialization (QR) ====

// Opens a short-lived ROP initialization session for the device (the `code`
// inside expires in about 1 minute). Admin only; not idempotent: every call
// mints a fresh code. The response is meant to be rendered as a QR payload.
export const createInitializationSession = async (uid) => {
  try {
    const response = await webBackApi.post(`/api/rop/devices/${uid}/initialization-session`);
    return response.data;
  } catch (error) {
    console.error("Error creating initialization session:", error);
    throw error;
  }
};
